<section class="bb-page" [dir]="isAr ? 'rtl' : 'ltr'">
  <div class="bb-page-shell">

    <!-- Header -->
    <div class="bb-page-header">
      <div class="bb-page-title-block">
        <span class="bb-eyebrow">
          {{ 'Manage Shifts' | transloco }}
        </span>

        <h2 class="bb-page-title">
          {{ 'Manage Shifts' | transloco }}
        </h2>
      </div>
    </div>

    @if (loading) {
      <div class="bb-card bb-p-16 bb-bg-blue-soft bb-text-blue bb-font-semibold bb-text-start">
        {{ 'clinicAppointments.loading' | transloco }}
      </div>
    }

    @if (!loading) {
      <div class="bb-shift-page-grid">

        <!-- Top Card -->
        <div class="bb-shift-top-card">
          <div class="bb-shift-top-inner">
            <div>
              <span class="bb-shift-kicker">
                {{ 'Base shift schedule' | transloco }}
              </span>

              <h3 class="bb-shift-top-title">
                {{ selectedBusinessName }}
              </h3>
            </div>

            <div class="bb-shift-top-actions">
              @if (usersLoading) {
                <span class="bb-text-muted bb-font-semibold">
                  {{ 'Loading users...' | transloco }}
                </span>
              }

              @if (hasSavedSchedule && viewMode === 'table') {
                <button class="bb-btn bb-btn-primary" type="button" (click)="openFormForEdit()">
                  {{ 'clinicAppointments.edit' | transloco }}
                </button>
              }
            </div>
          </div>
        </div>

        @if (serverError) {
          <div class="bb-form-alert">
            {{ serverError }}
          </div>
        }

        @if (usersLoadError) {
          <div class="bb-card bb-p-16 bb-bg-soft bb-text-warning bb-font-semibold bb-text-start">
            {{ usersLoadError }}
          </div>
        }

        <!-- Table View -->
        @if (viewMode === 'table') {
          <div class="bb-table-card bb-p-16">
            <div class="bb-table-scroll">
              <table class="bb-table table">
                <thead>
                  <tr>
                    <th>{{ 'clinicAppointments.day' | transloco }}</th>
                    <th>{{ 'clinicAppointments.workingHours' | transloco }}</th>
                  </tr>
                </thead>

                <tbody>
                  @for (day of savedSchedule; track trackByDay($index, day)) {
                    <tr>
                      <td>
                        <strong class="bb-text-navy">
                          {{ ('clinicAppointments.days.' + day.day) | transloco }}
                        </strong>
                      </td>

                      <td>
                        @if (!day.enabled) {
                          <span class="bb-badge bb-badge-danger">
                            {{ 'clinicAppointments.closed' | transloco }}
                          </span>
                        } @else if (!day.shifts.length) {
                          <span class="bb-badge bb-badge-warning">
                            {{ 'clinicAppointments.notConfigured' | transloco }}
                          </span>
                        } @else {
                          <div class="bb-shift-table-shifts">
                            @if (day.is24Hours) {
                              <span class="bb-badge bb-badge-soft-blue">
                                {{ '24 hours' | transloco }}
                              </span>
                            }

                            @for (shift of day.shifts; track trackByShift($index)) {
                              <div class="bb-shift-table-item">
                                <strong>
                                  @if (day.is24Hours) {
                                    {{ 'All day' | transloco }}
                                  } @else {
                                    {{ formatShiftText(shift) }}
                                  }
                                </strong>

                                <div class="bb-inline-flex bb-items-center bb-wrap bb-gap-8">
                                  @if (getShiftUserNames(shift).length) {
                                    @for (name of getShiftUserNames(shift); track name) {
                                      <span class="bb-shift-user-chip">
                                        {{ name }}
                                      </span>
                                    }
                                  } @else {
                                    <span class="bb-text-muted bb-font-semibold">
                                      {{ 'No users' | transloco }}
                                    </span>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }

        <!-- Form View -->
        @if (viewMode === 'form') {
          <form (ngSubmit)="onSubmit()" class="bb-shift-page-grid">
            @if (submitted && !isScheduleValid()) {
              <div class="bb-form-alert">
                {{ 'clinicAppointments.validation.reviewSchedule' | transloco }}
              </div>
            }

            <div class="bb-shift-page-grid">
              @for (day of draftSchedule; track trackByDay($index, day)) {
                <div
                  class="bb-shift-day-card"
                  [class.is-24]="day.is24Hours"
                  [class.has-error]="hasDayError(day)"
                >
                  <div class="bb-shift-day-head">
                    <div>
                      <h4 class="bb-shift-day-title">
                        {{ ('clinicAppointments.days.' + day.day) | transloco }}
                      </h4>

                      <small class="bb-shift-day-subtitle">
                        @if (day.enabled) {
                          @if (day.is24Hours) {
                            {{ 'Open 24 hours' | transloco }}
                          } @else {
                            {{ 'clinicAppointments.openDay' | transloco }}
                          }
                        } @else {
                          {{ 'clinicAppointments.closedDay' | transloco }}
                        }
                      </small>
                    </div>

                    <label class="bb-switch">
                      <input
                        type="checkbox"
                        [checked]="day.enabled"
                        (change)="toggleDay(day)" />
                      <span></span>
                    </label>
                  </div>

                  @if (day.enabled) {
                    <div class="bb-shift-option-row">
                      <div>
                        <strong>
                          {{ 'Open 24 hours' | transloco }}
                        </strong>

                        <small>
                          {{ 'If enabled, no need to select open and close times' | transloco }}
                        </small>
                      </div>

                      <label class="bb-switch bb-switch-sm">
                        <input
                          type="checkbox"
                          [checked]="day.is24Hours"
                          (change)="toggle24Hours(day)" />
                        <span></span>
                      </label>
                    </div>

                    @if (day.is24Hours) {
                      <div class="bb-shift-all-day-box">
                        <div>
                          <strong>
                            {{ 'All day schedule' | transloco }}
                          </strong>

                          <small>
                            {{ 'This day will be saved from 00:00 to 23:59' | transloco }}
                          </small>
                        </div>

                        @if (day.shifts.length) {
                          <div class="bb-shift-users-field bb-mt-16">
                            <label class="bb-shift-label">
                              {{ 'Users responsible for this day' | transloco }}
                            </label>

                            <button
                              class="bb-shift-users-btn"
                              type="button"
                              [disabled]="usersLoading"
                              (click)="openUsersPopup(day, 0)">
                              <span>
                                {{ 'Choose shift employees' | transloco }}
                              </span>

                              <strong>
                                {{ getSelectedUsersLabel(day.shifts[0]) }}
                              </strong>
                            </button>

                            <small class="bb-shift-users-hint">
                              {{ getSelectedUsersLabel(day.shifts[0]) }}
                            </small>

                            @if (getShiftUserNames(day.shifts[0]).length) {
                              <div class="bb-shift-selected-users">
                                @for (name of getShiftUserNames(day.shifts[0]); track name) {
                                  <span class="bb-shift-user-chip">
                                    {{ name }}
                                  </span>
                                }
                              </div>
                            }
                          </div>
                        }
                      </div>
                    } @else {
                      <div>
                        @for (shift of day.shifts; track trackByShift($index)) {
                          <div class="bb-shift-row-card">
                            <div class="bb-shift-row-grid">
                              <div class="bb-shift-time-field">
                                <label class="bb-shift-label">
                                  {{ 'clinicAppointments.openAt' | transloco }}
                                </label>

                                <div class="bb-shift-time-picker">
                                  <select
                                    [value]="getTimeHour12(shift.start)"
                                    (change)="updateArabicTimePart(day, $index, 'start', 'hour', $event)">
                                    @for (hour of hourOptions; track hour) {
                                      <option [value]="hour">{{ getTimeOptionLabel(hour) }}</option>
                                    }
                                  </select>

                                  <span>:</span>

                                  <select
                                    [value]="getTimeMinute(shift.start)"
                                    (change)="updateArabicTimePart(day, $index, 'start', 'minute', $event)">
                                    @for (minute of minuteOptions; track minute) {
                                      <option [value]="minute">{{ getTimeOptionLabel(minute) }}</option>
                                    }
                                  </select>

                                  <select
                                    [value]="getTimePeriod(shift.start)"
                                    (change)="updateArabicTimePart(day, $index, 'start', 'period', $event)">
                                    <option value="am">{{ getPeriodOptionLabel('am') }}</option>
                                    <option value="pm">{{ getPeriodOptionLabel('pm') }}</option>
                                  </select>
                                </div>

                                <small class="bb-shift-time-preview" *ngIf="isAr">
                                  {{ formatTime(shift.start) }}
                                </small>
                              </div>

                              <div class="bb-shift-time-field">
                                <label class="bb-shift-label">
                                  {{ 'clinicAppointments.closeAt' | transloco }}
                                </label>

                                <div class="bb-shift-time-picker">
                                  <select
                                    [value]="getTimeHour12(shift.end)"
                                    (change)="updateArabicTimePart(day, $index, 'end', 'hour', $event)">
                                    @for (hour of hourOptions; track hour) {
                                      <option [value]="hour">{{ getTimeOptionLabel(hour) }}</option>
                                    }
                                  </select>

                                  <span>:</span>

                                  <select
                                    [value]="getTimeMinute(shift.end)"
                                    (change)="updateArabicTimePart(day, $index, 'end', 'minute', $event)">
                                    @for (minute of minuteOptions; track minute) {
                                      <option [value]="minute">{{ getTimeOptionLabel(minute) }}</option>
                                    }
                                  </select>

                                  <select
                                    [value]="getTimePeriod(shift.end)"
                                    (change)="updateArabicTimePart(day, $index, 'end', 'period', $event)">
                                    <option value="am">{{ getPeriodOptionLabel('am') }}</option>
                                    <option value="pm">{{ getPeriodOptionLabel('pm') }}</option>
                                  </select>
                                </div>

                                <small class="bb-shift-time-preview" *ngIf="isAr">
                                  {{ formatTime(shift.end) }}
                                </small>
                              </div>

                              <div class="bb-shift-users-field">
                                <label class="bb-shift-label">
                                  {{ 'Users' | transloco }}
                                </label>

                                <button
                                  class="bb-shift-users-btn"
                                  type="button"
                                  [disabled]="usersLoading"
                                  (click)="openUsersPopup(day, $index)">
                                  <span>
                                    {{ 'Choose shift employees' | transloco }}
                                  </span>

                                  <strong>
                                    {{ getSelectedUsersLabel(shift) }}
                                  </strong>
                                </button>

                                <small class="bb-shift-users-hint">
                                  {{ getSelectedUsersLabel(shift) }}
                                </small>
                              </div>

                              <button
                                class="bb-shift-delete-btn"
                                type="button"
                                (click)="removeShift(day, $index)"
                                [attr.aria-label]="'clinicAppointments.deleteShift' | transloco">
                                ×
                              </button>
                            </div>

                            @if (getShiftUserNames(shift).length) {
                              <div class="bb-shift-selected-users">
                                @for (name of getShiftUserNames(shift); track name) {
                                  <span class="bb-shift-user-chip">
                                    {{ name }}
                                  </span>
                                }
                              </div>
                            }

                            @if (isOvernightShift(shift)) {
                              <div class="bb-field-hint bb-mt-8">
                                {{ 'clinicAppointments.nextDay' | transloco }}
                              </div>
                            }
                          </div>
                        }
                      </div>

                      @if (hasDayError(day)) {
                        <div class="bb-field-error">
                          {{ 'clinicAppointments.validation.dayTimeRequired' | transloco }}
                        </div>
                      }

                      <button class="bb-shift-add-btn" type="button" (click)="addShift(day)">
                        + {{ 'clinicAppointments.addShift' | transloco }}
                      </button>
                    }
                  } @else {
                    <div class="bb-shift-closed-box">
                      {{ 'clinicAppointments.dayClosedHint' | transloco }}
                    </div>
                  }
                </div>
              }
            </div>

            <div class="bb-modal-actions">
              <button
                class="bb-btn bb-btn-primary"
                type="submit"
                [disabled]="saving"
                [class.is-disabled]="saving">
                @if (saving) {
                  {{ 'clinicAppointments.saving' | transloco }}
                } @else {
                  {{ 'clinicAppointments.submit' | transloco }}
                }
              </button>
            </div>
          </form>
        }
      </div>
    }

  </div>
</section>

@if (usersPopupOpen) {
  <div class="bb-modal-backdrop" (click)="closeUsersPopup()">
    <div class="bb-modal-card bb-client-popup-card" (click)="$event.stopPropagation()">

      <div class="bb-modal-header">
        <div>
          <h4>
            {{ 'Choose shift employees' | transloco }}
          </h4>

          <small class="bb-field-hint">
            {{ getPopupSelectedUsersLabel() }}
          </small>
        </div>

        <button class="bb-modal-close" type="button" (click)="closeUsersPopup()">
          ×
        </button>
      </div>

      <div class="bb-input-icon-wrap bb-mb-12">
        <input
          class="bb-input"
          type="text"
          [value]="userSearchValue"
          [placeholder]="'Search employee name' | transloco"
          (input)="onUserSearchChange($event)" />

        @if (userSearchValue) {
          <button class="bb-scan-btn" type="button" (click)="clearUserSearch($event)">
            X
          </button>
        }
      </div>

      @if (popupTempUserIds.length) {
        <div class="bb-shift-selected-users bb-mb-12">
          @for (id of popupTempUserIds; track id) {
            <span class="bb-shift-user-chip">
              {{ getUserName(id) }}

              <button type="button" (click)="removePopupSelectedUser(id, $event)">
                ×
              </button>
            </span>
          }
        </div>
      }

      <div class="bb-users-popup-list">
        @if (filteredProviders.length) {
          @for (user of filteredProviders; track trackByUser($index, user)) {
            <label class="bb-users-popup-row">
              <input
                class="bb-checkbox"
                type="checkbox"
                [checked]="isPopupUserChecked(user.id)"
                (change)="togglePopupUser(user.id, $event)" />

              <span class="bb-client-avatar">
                {{ getUserInitial(user) }}
              </span>

              <span class="bb-text-stack">
                <strong class="bb-text-navy">
                  {{ getUserOptionLabel(user) }}
                </strong>

                @if (getUserMeta(user)) {
                  <small>
                    {{ getUserMeta(user) }}
                  </small>
                }
              </span>
            </label>
          }
        } @else {
          <div class="bb-empty-state">
            {{ 'No employees found' | transloco }}
          </div>
        }
      </div>

      <div class="bb-modal-actions">
        <button class="bb-btn bb-btn-secondary" type="button" (click)="closeUsersPopup()">
          {{ 'Cancel' | transloco }}
        </button>

        <button class="bb-btn bb-btn-primary" type="button" (click)="saveUsersPopup()">
          {{ 'Confirm selection' | transloco }}
        </button>
      </div>

    </div>
  </div>
}